import React, {
	useCallback, useEffect, useRef, useState,
} from 'react';
import {
	CheckCircleTwoTone, ExclamationCircleTwoTone,
	CloseCircleTwoTone, LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import { Space, Button, Modal } from 'antd-mobile';
import { message } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import { AttemptCoding } from '../mobile-components/attempt-coding-mobile';
import { AttemptMCQ } from '../mobile-components/attempt-mcq-mobile';
import { AttemptMQ } from '../mobile-components/attempt-mq-mobile';
import { AttemptSidebarSegment } from '../mobile-components/attempt-sidebar-segment-mobile';
import { AttemptSubjective } from '../mobile-components/attempt-subjective-mobile';
import { AttemptWeb } from '../mobile-components/attempt-web-mobile';
import {
	CheatingType, QuestionExecutionType, QuestionType, TestCaseStatus,
} from '../../config';
import { logger } from '../../libs/utils/logger';
import { useAppStore, useQuizStore, useSessionStore } from '../../store';

// import './quiz-attempt.scoped.css';
import { AttemptQuestionNotLoaded } from '../mobile-components/attempt-question-not-loaded-mobile';

const appStoreSelector = (state: AppState) => ({
	setAppLoading: state.setAppLoading,
});

const quizStoreSelector = (state: QuizState) => ({
	quizData: state.quizData,
	quizDashboardData: state.quizDashboardData,
	submittingQuestion: state.submittingQuestion,
	compiling: state.compiling,
	attemptQuestionData: state.attemptQuestionData,
	nextLink: state.nextLink,
	previousLink: state.previousLink,
	getAttemptQuestionData: state.getAttemptQuestionData,
	removeAttemptQuestionData: state.removeAttemptQuestionData,
	compileCode: state.compileCode,
	setQuestionTestCaseStatus: state.setQuestionTestCaseStatus,
	submitQuestion: state.submitQuestion,
	sendCheatingDetails: state.sendCheatingDetails,
	uploadSubjectiveFiles: state.uploadSubjectiveFiles,
});

const sessionStoreSelector = (state: SessionState) => ({
	sessionData: state.sessionData,
});

interface QuizAttemptScreenProps {
	quizName: string
	questionId: string
}

export const QuizAttemptScreen: React.FunctionComponent<QuizAttemptScreenProps> = (props) => {
	const { questionId, quizName } = props;

	const history = useHistory();

	const { setAppLoading } = useAppStore(appStoreSelector);
	const { sessionData } = useSessionStore(sessionStoreSelector);
	const {
		quizData,
		nextLink,
		previousLink,
		submittingQuestion,
		compiling,
		quizDashboardData,
		attemptQuestionData, compileCode,
		getAttemptQuestionData,
		removeAttemptQuestionData,
		setQuestionTestCaseStatus,
		submitQuestion,
		sendCheatingDetails,
		uploadSubjectiveFiles,
	} = useQuizStore(quizStoreSelector);

	const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);
	const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth <= 1100);
	const [questionLoadingErrorMessage, setQuestionLoadingErrorMessage] = useState<string>('');
	const [isUploadingFiles, setUploadingFiles] = useState<boolean>(false);

	const confirmModalRef = useRef<any>(null);

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 768px)');
		const legacyMediaQuery = mediaQuery as MediaQueryList & {
			addListener?: (listener: (ev: MediaQueryListEvent) => void) => void,
			removeListener?: (listener: (ev: MediaQueryListEvent) => void) => void,
		};
		const handleScreenChange = (ev: MediaQueryListEvent | MediaQueryList) => {
			setIsSmallScreen(ev.matches);
		};

		handleScreenChange(mediaQuery);
		if ('addEventListener' in mediaQuery) {
			mediaQuery.addEventListener('change', handleScreenChange);
		} else if (legacyMediaQuery.addListener) {
			legacyMediaQuery.addListener(handleScreenChange);
		}

		return () => {
			if ('removeEventListener' in mediaQuery) {
				mediaQuery.removeEventListener('change', handleScreenChange);
			} else if (legacyMediaQuery.removeListener) {
				legacyMediaQuery.removeListener(handleScreenChange);
			}
		};
	}, []);

	useEffect(() => {
		if (isSmallScreen) {
			setSidebarVisible(false);
		}
	}, [isSmallScreen]);

	useEffect(() => {
		if (attemptQuestionData?.hasBeenInvalidated) {
			message.warn('Uh Oh! This test has been updated, redirecting you to the dashboard.', 3);
			if (nextLink) {
				history.replace(nextLink);
			}
		}
	}, [history, attemptQuestionData?.hasBeenInvalidated, nextLink]);

	// useEffect(() => {
	// 	if (attemptQuestionData?._id
	// 		&& !quizDashboardData?.questionIds.includes(attemptQuestionData?._id)) {
	// 		message.warn(
	// 			'Uh Oh! This question has been moved/updated, redirecting you to the dashboard.', 3,
	// 		);
	// 		if (nextLink) {
	// 			history.replace(nextLink);
	// 		}
	// 	}
	// }, [history, attemptQuestionData?._id, quizDashboardData?.questionIds, nextLink]);

	useEffect(() => {
		if (attemptQuestionData?.isSegmentSubmitted) {
			message.warn('Question segment has already been submitted, redirecting you to the dashboard.', 1);
			if (nextLink) {
				history.replace(nextLink);
			}
		}
	}, [history, attemptQuestionData?.isSegmentSubmitted, nextLink]);

	useEffect(() => {
		(async () => {
			try {
				setQuestionLoadingErrorMessage('');
				await setAppLoading(false);
				// await setAppLoading(true, 'getting question data...');
				await setAppLoading(true);

				await getAttemptQuestionData(questionId);
			} catch (e: any) {
				logger.error(e);
				setQuestionLoadingErrorMessage(e.message ?? e ?? '');
				message.error(e.message);
			} finally {
				setAppLoading(false);
			}
		})();

		return () => removeAttemptQuestionData();
	}, [
		questionId, setQuestionLoadingErrorMessage,
		getAttemptQuestionData, removeAttemptQuestionData,
		setAppLoading,
	]);

	const setAttempt = useCallback((attemptData: unknown) => {
		window.localStorage.setItem(`${quizData?.quizId}:${sessionData?.userId}:${questionId}`, JSON.stringify(attemptData));
	}, [questionId, quizData, sessionData]);

	const getAttempt = useCallback(() => {
		const data = window.localStorage.getItem(`${quizData?.quizId}:${sessionData?.userId}:${questionId}`);
		if (data) {
			return JSON.parse(data);
		}

		return null;
	}, [questionId, quizData?.quizId, sessionData?.userId]);

	const uploadFiles = useCallback(async (files: Array<any>) => {
		try {
			setUploadingFiles(true);
			if (!quizData?.quizId || !sessionData?.userId) {
				throw new Error('Please login first');
			}
			const result = await uploadSubjectiveFiles({
				questionId,
				quizId: quizData.quizId,
				userId: sessionData.userId,
			}, files);
			return result;
		} catch (error) {
			logger.error(error);
			return { error: 'The files could not be uploaded due to an error. Please try again in a few moments.' };
		} finally {
			setUploadingFiles(false);
		}
	}, [questionId, quizData?.quizId, sessionData?.userId, uploadSubjectiveFiles, setUploadingFiles]);

	const retryQuestionGet = useCallback(async () => {
		try {
			await setAppLoading(true);
			await getAttemptQuestionData(questionId);
		} catch (e: any) {
			logger.error(e);
			setQuestionLoadingErrorMessage(e.message ?? e ?? '');
			message.error(e.message);
		} finally {
			setAppLoading(false);
		}
	}, [questionId, getAttemptQuestionData, setAppLoading]);

	const handleSubmitQuestion = useCallback(async (data: any) => {
		try {
			await submitQuestion({
				...data,
				sid: sessionData?.sessionId,
			});

			message.success('Your Answer is submitted successfully', 3);

			if (attemptQuestionData?.type && +attemptQuestionData.type === QuestionType.MCQ && nextLink) {
				history.push(nextLink);
			}
		} catch (e: any) {
			logger.error(e);

			message.error(e.message);
		}
	}, [history, sessionData, attemptQuestionData, nextLink, submitQuestion]);

	const goToNextQuestion = useCallback(() => {
		if (nextLink) {
			history.push(nextLink);
		}
	}, [history, nextLink]);

	const goToPreviousQuestion = useCallback(() => {
		if (previousLink) {
			history.push(previousLink);
		}
	}, [history, previousLink]);

	const compileCodeCallback = useCallback(async (data: any) => {
		if (!navigator.onLine) {
			if (confirmModalRef.current) confirmModalRef.current.destroy();
			message.error('Please check your internet connection.');
			return;
		}
		if (!sessionData) {
			throw new Error('session data not present.');
		}

		try {
			await compileCode({
				...data,
				sid: sessionData.sessionId,
			});
		} catch (err: any) {
			message.error(err.message);
			logger.log('Error when submit....', err.message);
			return;
		}

		if (data.questionSubmission && navigator.onLine) {
			// message.success('Submission success!', 2);
			if (confirmModalRef.current) {
				confirmModalRef.current.destroy();
			}
			confirmModalRef.current = Modal.confirm({
				content: (
					<div>
						<div>
							<ExclamationCircleTwoTone twoToneColor="#faad14" style={{ fontSize: 32 }} />
						</div>
						<div style={{ marginTop: '0.5rem' }}>
							<span>Submission success! do you want to proceed to next question?</span>
						</div>
						<div style={{ marginTop: '0.75rem' }}>
							<Button
								type="button"
								loading
								style={{
									backgroundColor: '#f7f7f7',
									padding: '0.5rem 0.75rem',
									height: 'unset',
								}}
							>
								Compiling
							</Button>
						</div>
					</div>
				),
				cancelText: 'stay',
				confirmText: 'next question',
				// icon: <CheckCircleOutlined style={{ color: 'green' }} />,
				onConfirm: goToNextQuestion,
			});
		}
	}, [sessionData, compileCode, goToNextQuestion]);

	useEffect(() => {
		// After submission if internet connection gone close modal

		window.addEventListener('offline', () => {
			if (confirmModalRef.current) {
				confirmModalRef.current.destroy();
			}
			message.error('Please check your internet connection.');
		});
	}, []);

	useEffect(() => {
		if (!compiling) {
			let isAllTestCasePassed = true;
			if (attemptQuestionData?.questionTypeCoding?.executionType
				&& +attemptQuestionData.questionTypeCoding.executionType
				!== QuestionExecutionType.CompileOnly
				&& !attemptQuestionData.questionTypeCoding?.consoleOutput?.error) {
				attemptQuestionData?.questionTypeCoding?.testCases.forEach((testCase) => {
					if (testCase.status === TestCaseStatus.Failed) {
						isAllTestCasePassed = false;
						return 0;
					}
					return 0;
				});
			}

			confirmModalRef.current?.update({
				content: (
					<div>
						<div>
							{attemptQuestionData?.questionTypeCoding?.consoleOutput?.error
								|| !isAllTestCasePassed ? (
									<CloseCircleTwoTone twoToneColor="#ff4d4f	" style={{ fontSize: 32 }} />
								) : (
									<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 32 }} />
								)}
						</div>
						<div style={{ marginTop: '0.5rem' }}>
							<span>Submission success! do you want to proceed to next question?</span>
						</div>

						{/* error */}
						{attemptQuestionData?.questionTypeCoding?.consoleOutput?.error && (
							<div style={{ marginTop: '1rem', marginBottom: '0.75rem' }}>
								<span
									style={{
										color: 'var(--primary-color)',
										padding: '0.5rem 0.75rem',
										backgroundColor: '#f7f7f7',
										borderRadius: 5,
									}}
								>
									Compile error
								</span>
							</div>
						)}
						{/* error */}

						{
							attemptQuestionData?.questionTypeCoding?.executionType
							&& +attemptQuestionData.questionTypeCoding.executionType
							!== QuestionExecutionType.CompileOnly
							&& !attemptQuestionData.questionTypeCoding?.consoleOutput?.error && (
								<div style={{ marginTop: '1rem', marginBottom: '0.75rem' }}>
									<span
										style={{
											color: 'var(--primary-color)',
											padding: '0.5rem 0.75rem',
											backgroundColor: '#f7f7f7',
											borderRadius: 5,
										}}
									>
										{
											`${attemptQuestionData?.questionTypeCoding?.testCases.filter(
												(testCase) => testCase.status === TestCaseStatus.Passed,
											).length} Passed, ${attemptQuestionData?.questionTypeCoding?.testCases.filter(
												(testCase) => testCase.status === TestCaseStatus.Failed,
											).length} Failed`
										}
									</span>
								</div>
							)
						}
					</div>
				),
			});
		}
	}, [compiling, attemptQuestionData]);

	const toggleSidebar = useCallback(() => {
		setSidebarVisible(!sidebarVisible);
	}, [sidebarVisible]);

	const sendPasteNotification = useCallback(() => {
		sendCheatingDetails(CheatingType.Paste);
	}, [sendCheatingDetails]);

	const renderQuestionDetailsPane = (questionData: AttemptQuestionData) => (
		<div style={{
			padding: '0 20px 20px 20px', backgroundColor: '#fff', backgroundImage: 'var(--watermark-background-image)', maxHeight: '400px', overflowY: 'auto',
		}}
		>
			<div style={{
				position: 'sticky', top: 0, zIndex: 1,
			}}
			>
				{/* Tag */}
				<div style={{
					display: 'inline-block',
					border: '1px solid #ffccba',
					backgroundColor: '#fff',
					color: '#de6834',
					padding: '2px 12px',
					borderRadius: '16px',
					fontSize: '12px',
					fontWeight: 600,
					marginTop: '16px',
				}}
				>
					{QuestionType[Number(questionData.type)]}
				</div>

				{/* Title */}
				<div style={{
					fontSize: '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px',
				}}
				>
					{questionData.title}
				</div>

				{/* Custom Underline */}
				<div
					style={{
						width: '40px',
						height: '4px',
						backgroundColor: '#de6834',
						borderRadius: '2px',
						marginBottom: '20px',
					}}
				/>

			</div>

			{/* Description */}
			<div
				className={`question-description ${quizData?.copyPasteAllowed ? 'selectable' : ''}`}
				style={{
					color: '#595959',
					fontSize: '15px',
					lineHeight: '1.6',
					overflowY: 'auto',
				}}
				dangerouslySetInnerHTML={{
					__html: questionData.text as string,
				}}
			/>
		</div>
	);

	const renderQuestionAttemptPane = (questionData: AttemptQuestionData) => (
		<div className={+questionData.type === QuestionType.MCQ ? 'question-attempt-pane-scroll' : 'question-attempt-pane'} style={{ paddingBottom: '80px', backgroundImage: 'var(--watermark-background-image)' }}>
			{
				(() => {
					if (+questionData.type === QuestionType.MCQ) {
						return (
							<AttemptMCQ
								questionData={questionData}
								onSubmit={handleSubmitQuestion}
								submitting={submittingQuestion || isUploadingFiles}
								setAttempt={setAttempt}
								getAttempt={getAttempt}
								quizData={quizData}
							/>
						);
					} if (+questionData.type === QuestionType.Subjective) {
						return (
							<AttemptSubjective
								disableCopyPaste={!quizData?.copyPasteAllowed}
								sendPasteNotification={sendPasteNotification}
								questionData={questionData}
								onSubmit={handleSubmitQuestion}
								submitting={submittingQuestion}
								setAttempt={setAttempt}
								getAttempt={getAttempt}
								uploadFiles={uploadFiles}
							/>
						);
					} if (+questionData.type === QuestionType.Coding) {
						if (!(questionData.questionTypeCoding?.codeLanguages?.length)) {
							return (
								<div style={{ width: '100%' }}>
									<h1 className="no-lang-text">
										Sorry, you cannot attempt this question
										because no language is given for this question ;)
									</h1>
								</div>
							);
						}
						return (
							<AttemptCoding
								disableCopyPaste={!quizData?.copyPasteAllowed}
								questionData={questionData}
								compileCode={compileCodeCallback}
								setTestCaseStatus={setQuestionTestCaseStatus}
								compiling={compiling}
								submitting={submittingQuestion || isUploadingFiles}
								setAttempt={setAttempt}
								getAttempt={getAttempt}
								sendPasteNotification={sendPasteNotification}
							/>
						);
					} if (+questionData.type === QuestionType.MQ) {
						return (
							<AttemptMQ
								disableCopyPaste={!quizData?.copyPasteAllowed}
								questionData={questionData}
								onSubmit={handleSubmitQuestion}
								submitting={submittingQuestion || isUploadingFiles}
								setAttempt={setAttempt}
								getAttempt={getAttempt}
								sendPasteNotification={sendPasteNotification}
							/>
						);
					} if (+questionData.type === QuestionType.Web) {
						return (
							<AttemptWeb
								disableCopyPaste={!quizData?.copyPasteAllowed}
								questionData={questionData}
								compileCode={compileCodeCallback}
								onSubmit={handleSubmitQuestion}
								setTestCaseStatus={setQuestionTestCaseStatus}
								compiling={compiling}
								submitting={submittingQuestion || isUploadingFiles}
								setAttempt={setAttempt}
								getAttempt={getAttempt}
								sendPasteNotification={sendPasteNotification}
							/>
						);
					}

					return null;
				})()
			}
		</div>
	);

	if (attemptQuestionData?.hasBeenInvalidated) {
		return (
			<div className="question-attempt-wrapper invalidated">
				<h1>Uh Oh! This question has been moved/updated.</h1>
				<div>
					{
						nextLink && (
							<Link
								replace
								to={nextLink}
							>
								Go back to Dashboard
							</Link>
						)
					}
				</div>
			</div>
		);
	}

	return (
		<div className="question-attempt-wrapper" style={{ overflow: 'auto', backgroundImage: 'var(--watermark-background-image)' }}>
			{
				sidebarVisible && (
					<div className="sidebar-container">
						{
							quizDashboardData?.segments.map((segment, index) => (
								<AttemptSidebarSegment
									key={segment._id}
									quizName={quizName}
									segmentData={segment}
									segmentIndex={index}
									isLocked={segment.isDisabled}
									isSubmitted={segment.isSubmitted}
									currentQuestionId={attemptQuestionData?._id ?? ''}
								/>
							))
						}
					</div>
				)
			}
			{
				!isSmallScreen && (
					<div className="sidebar-toggler">
						<Button
							type="button"
							size="small"
							// icon={
							// 	sidebarVisible
							// 		? <LeftOutlined style={{ color: 'var(--primary-color)' }} />
							// 		: <RightOutlined style={{ color: 'var(--primary-color)' }} />
							// }
							onClick={toggleSidebar}
						/>
					</div>
				)
			}
			{
				attemptQuestionData && (
					<>
						{renderQuestionDetailsPane(attemptQuestionData)}
						{renderQuestionAttemptPane(attemptQuestionData)}
						<div
							style={{
								position: 'fixed',
								bottom: 0,
								left: 0,
								width: '100%',
								backgroundColor: '#fff',
								borderTop: '1px solid #f0f0f0',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								padding: '0.5em',
								zIndex: 99,
							}}
						>
							<Space
								tabIndex={0}
								style={{
									color: previousLink ? '#de6834' : '#ccc',
									fontSize: '16px',
									gap: '4px',
									cursor: previousLink ? 'pointer' : 'default',
								}}
								onClick={previousLink ? goToPreviousQuestion : undefined}
							>
								<LeftOutlined style={{ fontSize: '12px' }} />
								previous
							</Space>

							<div id="mobile-submit-portal" style={{ flex: 1, display: 'flex', justifyContent: 'center' }} />

							<Space
								tabIndex={0}
								style={{
									color: nextLink ? '#de6834' : '#ccc',
									fontSize: '16px',
									gap: '4px',
									cursor: nextLink ? 'pointer' : 'default',
								}}
								onClick={nextLink ? goToNextQuestion : undefined}
							>
								next
								<RightOutlined style={{ fontSize: '12px' }} />
							</Space>
						</div>
					</>
				)
			}
			{
				!attemptQuestionData && questionLoadingErrorMessage && (
					<AttemptQuestionNotLoaded
						retryFunc={retryQuestionGet}
						errorMessage={questionLoadingErrorMessage}
					/>
				)
			}
		</div>
	);
};
